/**
 * Light-markdown renderer for CHAT bubbles — shared by the portal `ChatAssistant`
 * and the operator `Ask` page.
 *
 * The LLM formats answers with `**bold**`, `## headings` and lists; rendering
 * them keeps the bubble readable instead of showing raw tokens. Styling is
 * COMPACT (the docs `MarkdownRenderer` uses doc-sized headings/margins that look
 * heavy in a chat bubble). react-markdown sanitizes hrefs and emits no raw HTML,
 * so it is safe even for portal end-users; external links open in a new tab.
 */
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { cn } from "@/lib/cn";

export function ChatMarkdown({
  text,
  className,
  onInternalNavigate,
}: {
  text: string;
  className?: string;
  /** When set, a `/`-rooted href becomes an SPA navigation instead of a new
   *  tab — the Odino rail's "apri l'editor" links (a new-tab full load would
   *  drop the rail + chat). External links keep target=_blank. */
  onInternalNavigate?: (path: string) => void;
}) {
  return (
    <div
      className={cn(
        "break-words",
        "[&>*:first-child]:mt-0 [&>*:last-child]:mb-0",
        "[&_p]:my-1.5",
        "[&_h1]:my-1 [&_h1]:text-sm [&_h1]:font-semibold",
        "[&_h2]:my-1 [&_h2]:text-sm [&_h2]:font-semibold",
        "[&_h3]:my-1 [&_h3]:text-sm [&_h3]:font-semibold",
        "[&_h4]:my-1 [&_h4]:text-sm [&_h4]:font-semibold",
        "[&_ul]:my-1.5 [&_ul]:list-disc [&_ul]:pl-4",
        "[&_ol]:my-1.5 [&_ol]:list-decimal [&_ol]:pl-4",
        "[&_li]:my-0.5",
        "[&_strong]:font-semibold",
        "[&_a]:text-accent [&_a]:underline [&_a]:underline-offset-2",
        "[&_code]:rounded [&_code]:bg-elevated [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-[0.85em]",
        "[&_table]:my-2 [&_table]:w-full [&_table]:border-collapse [&_table]:text-xs",
        "[&_th]:border-b [&_th]:border-border [&_th]:px-1.5 [&_th]:py-1 [&_th]:text-left [&_th]:font-medium",
        "[&_td]:border-b [&_td]:border-border/50 [&_td]:px-1.5 [&_td]:py-1 [&_td]:align-top",
        "[&_hr]:my-2 [&_hr]:border-border",
        className,
      )}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          a: ({ children, href, ...props }) => {
            if (onInternalNavigate && typeof href === "string" && href.startsWith("/")) {
              return (
                <a
                  {...props}
                  href={href}
                  onClick={(e) => {
                    e.preventDefault();
                    onInternalNavigate(href);
                  }}
                >
                  {children}
                </a>
              );
            }
            return (
              <a {...props} href={href} target="_blank" rel="noreferrer">
                {children}
              </a>
            );
          },
        }}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
}
